//Helpers
import { is_gen_2 } from 'src/helpers/interface_helpers';
import { devices_settings } from 'src/stores/device-runtime/status';
import { devices_config } from '@devices/index';
import { getComponentByDeviceMode } from '../../local/core/local_action_components_helpers';
import {
	findDevicesByChannel,
	findDevicesByWebhookIP,
	getAvailableWebhookIP,
} from '../core/webhook_devices_helpers';
import { getURLWebhookParams } from '../core/webhook_parse_url_helpers';
import { getMinBrightnessByDevice } from '../core/webhook_helpers_gen1';
//Types
import type { Device } from 'src/types/Device';
import type { ActionJob } from 'src/types/requests/schedules/schedules_g2';

export const validateURLWebhookParams = (action: ActionJob, device: Device): ActionJob => {
	const deviceGen = is_gen_2(device) ? 2 : 1;
	const method = (action.method || '').toLowerCase();
	for (let k in action.params) {
		k = k.toLowerCase();
		let param = action.params[k];
		if (action.error) break;
		if (k == 'id') continue;
		if (typeof param === 'object' && /^(config)$/.test(k)) {
			action.error = !(
				/^(rgbcct.setconfig)/.test(method) && /^(rgb|cct|rgbcct)$/.test(param?.mode)
			);
		} else if (/\s/.test(param) || /\s/.test(k) || param === '') {
			action.error = true;
		} else if (/^(on|output)$/.test(k) && !/(cover|toggle)/.test(method)) {
			action.error = typeof param !== 'boolean';
		} else if (/^(toggle_after)$/.test(k) && !/^(cover)/.test(method)) {
			action.error =
				!(typeof param === 'number' && Number.isSafeInteger(param) && param >= 0) &&
				!isValidActionToken(param);
		} else if (/^(pos|slat_pos)$/.test(k) && /^(cover.gotoposition)$/.test(method)) {
			//slat_pos is gen 2++ tilt prop
			action.error = !(param >= 0 && param <= 100) && !isValidActionToken(param);
		} else if (/^(slat_rel)$/.test(k) && /^(cover.gotoposition)$/.test(method)) {
			//slat_rel is gen 2++ tilt prop from -100 to 100
			action.error = !(param >= -100 && param <= 100) && !isValidActionToken(param);
		} else if (
			/^(transition_duration)$/.test(k) &&
			/^(light|cct|rgbcct|rgbw?)/.test(method) &&
			deviceGen == 2 &&
			device.type != 'SNDM-0013US'
			//Plus Wall Dimmer
		) {
			action.error = !(param >= 0.5 && param <= 10800) && !isValidActionToken(param);
		} else if (/^(brightness)$/.test(k) && /^(light|cct|rgbcct|rgbw?)/.test(method)) {
			action.error =
				!(param >= getMinBrightnessByDevice(device) && param <= 100) &&
				!isValidActionToken(param);
		} else if (/^(offset|offset_white)$/.test(k) && /^(light|cct|rgbcct|rgbw?)/.test(method)) {
			action.error = !(param >= -100 && param <= 100) && !isValidActionToken(param);
		} else if (/^(white)$/.test(k) && /^(light|cct|rgbcct|rgbw?)/.test(method)) {
			action.error = !(param >= 0 && param <= 255) && !isValidActionToken(param);
		} else if (/^(ct)$/.test(k) && /^(light|cct|rgbcct)\./.test(method)) {
			const mode = getComponentByDeviceMode(device).deviceMode;
			const valueRange = devices_config[device.type]?.modes[mode]?.color_temp;
			action.error =
				!valueRange || !(param >= valueRange?.minValue && param <= valueRange?.maxValue);
		} else if (/^(rgb)$/.test(k) && /^(rgbcct|rgbw?)/.test(method)) {
			action.error =
				!param?.length ||
				param.filter((i) => isValidActionToken(i) || (i >= 0 && i <= 255)).length != 3;
		} else {
			action.error = true;
		}
	}
	return action;
};

export const validateVirtualURLWebhookParams = (action: ActionJob, device: Device): ActionJob => {
	const method = (action.method || '').toLowerCase();
	for (let k in action.params) {
		k = k.toLowerCase();
		let param = action.params[k];
		if (action.error) break;
		if (k == 'id') continue;
		const deviceSetting = devices_settings.getByRealId(device.id);
		const component: string = `${method.split('.')[0]}:${action?.params?.id}`;
		const config = deviceSetting?.[component];
		if (!config || /\s/.test(param) || /\s/.test(k) || param === '') {
			action.error = true;
		} else if (/^(button.trigger)$/.test(method) && /^(event)$/.test(k)) {
			action.error = !/^(single_push|double_push|triple_push|long_push)$/i.test(param);
		} else if (/^(number.set)$/.test(method) && /^(value)$/.test(k)) {
			action.error =
				(typeof param !== 'number' || param < config?.min || param > config?.max) &&
				!isValidActionToken(param);
		} else if (/^(boolean.set)$/.test(method) && /^(value)$/.test(k)) {
			action.error = !/^(true|false)$/i.test(param);
		} else if (/^(text.set)$/.test(method) && /^(value)$/.test(k)) {
			action.error = /^(null|undefined|NaN)$/.test(param);
		} else if (/^(enum.set)$/.test(method) && /^(value)$/.test(k)) {
			action.error =
				!Array.isArray(config?.options) ||
				!config.options.some((option) => String(option) === String(param));
		} else {
			action.error = true;
		}
	}
	return action;
};

export const validateLnmURLWebhookParams = (action: ActionJob, device: Device): ActionJob => {
	const actionParams = action.params || {};
	const { id, method = '' } = actionParams;
	const params = actionParams.params || {};
	const isSet = /\.set$/i.test(method);
	const invalidRemoteParam = Object.keys(params).some(
		(key) => key !== 'id' && !(isSet && key === 'on'),
	);

	action.error =
		!devices_settings.getByRealId(device.id)?.[`lnm:${id}`] ||
		!/^(switch|light|cct|rgb|rgbw|rgbcct)\.(set|toggle)$/i.test(method) ||
		!Number.isInteger(params.id) ||
		params.id < 0 ||
		invalidRemoteParam ||
		(isSet && typeof params.on !== 'boolean' && !isValidActionToken(String(params.on)));
	return action;
};

export const isValidActionToken = (param: string) => {
	return /^-?\$\{ev\.\w+\}$/.test(param);
};

export const isValidWebhookUrl = (urls: string[]): boolean => {
	let isValid = true;
	(urls || []).forEach((i) => {
		if (!/http:\/\/(127.0.0.1|localhost)\/rpc\//.test(i)) {
			const ip = getAvailableWebhookIP(i);
			const devices = findDevicesByWebhookIP(ip);
			if (devices.length) {
				for (let d = 0; d < devices.length; ++d) {
					const device: Device = devices[d];
					const action: ActionJob = getURLWebhookParams(i, device);
					if (findDevicesByChannel(device, action) && action.error) {
						isValid = false;
						return;
					}
				}
			}
			// ToDO: Valid webhook and IP address, but the device does not exist in the App.
			// else if (ip && /(\/rpc\/([^\?]+)|(\d\/(relay|roller|light|color)\/(\d+\b)\/?))/.test(i)) {
			// 	isInvalid = true;
			// 	return;
			// }
		}
	});
	return isValid;
};

export const validateLocalActionValue = (value: any, min: number, max: number): boolean => {
	return !isValidActionToken(value) && (value == null || isNaN(value) || value < min || value > max);
};
